<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { usePermissions } from 'dashboard/composables/usePermissions';

const store = useStore();
const { isSuperAdmin, isAdminOrSuperAdmin } = usePermissions();
const branches = computed(() => store.getters['branches/getBranches'] || []);

const showModal = ref(false);
const isEditing = ref(false);
const isSaving = ref(false);
const form = ref({ id: null, name: '', code: '', active: true });
const errorMessage = ref('');

function openCreate() {
  isEditing.value = false;
  form.value = { id: null, name: '', code: '', active: true };
  errorMessage.value = '';
  showModal.value = true;
}
function openEdit(branch) {
  isEditing.value = true;
  form.value = {
    id: branch.id,
    name: branch.name,
    code: branch.code,
    active: branch.active,
    logo_url: branch.logo_url,
    is_personal: branch.is_personal,
  };
  errorMessage.value = '';
  showModal.value = true;
}

async function onLogoChange(event) {
  const file = event?.target?.files?.[0];
  if (!file || !form.value.id) return;
  try {
    const updated = await store.dispatch('branches/uploadLogo', { id: form.value.id, file });
    form.value.logo_url = updated.logo_url;
    useAlert('Logo actualizado');
  } catch (err) {
    useAlert(err?.response?.data?.error || 'Error al subir el logo');
  }
}

async function removeLogo() {
  if (!form.value.id) return;
  // eslint-disable-next-line no-alert
  if (!window.confirm('¿Eliminar el logo de esta sucursal?')) return;
  try {
    const updated = await store.dispatch('branches/removeLogo', form.value.id);
    form.value.logo_url = updated.logo_url;
    useAlert('Logo eliminado');
  } catch (err) {
    useAlert('Error al eliminar el logo');
  }
}
function closeModal() {
  showModal.value = false;
}

async function save() {
  if (!form.value.name.trim() || !form.value.code.trim()) {
    errorMessage.value = 'Nombre y código son obligatorios';
    return;
  }
  isSaving.value = true;
  errorMessage.value = '';
  try {
    if (isEditing.value) {
      await store.dispatch('branches/update', {
        id: form.value.id,
        name: form.value.name.trim(),
        code: form.value.code.trim().toUpperCase(),
        active: form.value.active,
      });
      useAlert('Sucursal actualizada');
    } else {
      await store.dispatch('branches/create', {
        name: form.value.name.trim(),
        code: form.value.code.trim().toUpperCase(),
        active: form.value.active,
      });
      useAlert('Sucursal creada');
    }
    closeModal();
  } catch (err) {
    errorMessage.value = err?.response?.data?.error || err?.message || 'Error al guardar';
  } finally {
    isSaving.value = false;
  }
}

async function remove(branch) {
  if (branch.is_personal) {
    useAlert('No se puede eliminar la sucursal Personal');
    return;
  }
  // eslint-disable-next-line no-alert
  if (!window.confirm(`¿Eliminar la sucursal "${branch.name}"? Esta acción no se puede deshacer.`)) return;
  try {
    await store.dispatch('branches/delete', branch.id);
    useAlert('Sucursal eliminada');
  } catch (err) {
    useAlert(err?.response?.data?.error || 'Error al eliminar');
  }
}

onMounted(() => {
  store.dispatch('branches/get');
});
</script>

<template>
  <div class="flex flex-col h-full p-6 bg-slate-50 dark:bg-slate-900">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-2xl font-bold text-slate-900 dark:text-white">Sucursales</h1>
        <p class="text-sm text-slate-500 mt-1">
          Cada sucursal aísla sus contactos, conversaciones, leads e inboxes.
        </p>
      </div>
      <button
        v-if="isAdminOrSuperAdmin"
        type="button"
        class="px-4 py-2 rounded-lg text-sm font-semibold text-black flex items-center gap-2"
        style="background: linear-gradient(135deg,#fbbf24,#d97706); box-shadow: 0 0 16px rgba(251,191,36,0.4);"
        @click="openCreate"
      >
        <span class="i-lucide-plus text-[16px]" />
        Nueva sucursal
      </button>
    </div>

    <!-- Lista -->
    <div class="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      <table class="w-full text-sm">
        <thead class="bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-400 uppercase tracking-wide text-[11px]">
          <tr>
            <th class="px-4 py-3 text-left font-semibold">Código</th>
            <th class="px-4 py-3 text-left font-semibold">Nombre</th>
            <th class="px-4 py-3 text-left font-semibold">Tipo</th>
            <th class="px-4 py-3 text-center font-semibold">Estado</th>
            <th class="px-4 py-3 text-right font-semibold">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="b in branches"
            :key="b.id"
            class="border-t border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50"
          >
            <td class="px-4 py-3">
              <span
                class="inline-flex items-center justify-center w-9 h-9 rounded-lg font-bold text-[12px]"
                style="background: rgba(251,191,36,0.15); color: #d97706; border: 1px solid rgba(251,191,36,0.4);"
              >{{ b.code }}</span>
            </td>
            <td class="px-4 py-3 font-semibold text-slate-900 dark:text-white">{{ b.name }}</td>
            <td class="px-4 py-3">
              <span
                v-if="b.is_personal"
                class="text-[11px] font-bold uppercase tracking-wide px-2 py-1 rounded"
                style="background: rgba(99,102,241,0.15); color: #6366f1;"
              >Personal</span>
              <span v-else class="text-[11px] text-slate-500">Sucursal</span>
            </td>
            <td class="px-4 py-3 text-center">
              <span
                class="text-[11px] font-bold uppercase tracking-wide px-2 py-1 rounded"
                :style="b.active
                  ? 'background: rgba(34,197,94,0.15); color: #16a34a;'
                  : 'background: rgba(148,163,184,0.15); color: #64748b;'"
              >{{ b.active ? 'Activa' : 'Inactiva' }}</span>
            </td>
            <td class="px-4 py-3 text-right">
              <button
                v-if="isAdminOrSuperAdmin && (!b.is_personal || isSuperAdmin)"
                type="button"
                class="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-500/10"
                title="Editar"
                @click="openEdit(b)"
              >
                <span class="i-lucide-pencil text-[16px]" />
              </button>
              <button
                v-if="isAdminOrSuperAdmin && !b.is_personal"
                type="button"
                class="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 ml-1"
                title="Eliminar"
                @click="remove(b)"
              >
                <span class="i-lucide-trash-2 text-[16px]" />
              </button>
            </td>
          </tr>
          <tr v-if="branches.length === 0">
            <td colspan="5" class="px-4 py-8 text-center text-slate-500 italic">
              Sin sucursales todavía
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal crear/editar -->
    <div
      v-if="showModal"
      class="fixed inset-0 z-50 flex items-center justify-center"
      style="background: rgba(0,0,0,0.6); backdrop-filter: blur(4px);"
      @click.self="closeModal"
    >
      <div
        class="w-full max-w-md rounded-xl p-6 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl"
      >
        <h2 class="text-lg font-bold text-slate-900 dark:text-white mb-4">
          {{ isEditing ? 'Editar sucursal' : 'Nueva sucursal' }}
        </h2>

        <div class="space-y-4">
          <!-- Upload de logo · solo cuando se edita una sucursal existente -->
          <div v-if="isEditing">
            <label class="block text-[12px] font-semibold text-slate-600 dark:text-slate-400 mb-1.5 uppercase tracking-wide">
              Logo de la sucursal
            </label>
            <div class="flex items-center gap-3">
              <!-- Preview -->
              <div
                class="w-[64px] h-[64px] rounded-xl flex flex-col items-center justify-center font-extrabold text-[11px] flex-shrink-0 overflow-hidden"
                :style="form.logo_url
                  ? 'background: #000;'
                  : (form.is_personal
                      ? 'background: radial-gradient(circle at 50% 100%, rgba(251,191,36,0.5), transparent 70%), linear-gradient(135deg,#fbbf24,#d97706); color: #000;'
                      : 'background: radial-gradient(circle at 50% 100%, rgba(251,191,36,0.5), transparent 70%), linear-gradient(135deg,#1a1a1a,#050505); color: #fbbf24;')"
                style="box-shadow: 0 0 0 2px rgba(251,191,36,0.40) inset;"
              >
                <img v-if="form.logo_url" :src="form.logo_url" :alt="form.name" class="w-full h-full object-cover" />
                <template v-else>
                  <span v-if="form.is_personal" class="text-[16px]">VG</span>
                  <template v-else>
                    <span class="text-[20px] leading-none">📷</span>
                    <span class="text-[10px] mt-0.5" style="letter-spacing: 0.6px;">{{ form.code }}</span>
                  </template>
                </template>
              </div>
              <!-- Acciones -->
              <div class="flex-1 space-y-2">
                <input
                  ref="logoInput"
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  class="hidden"
                  @change="onLogoChange"
                />
                <button
                  type="button"
                  class="w-full px-3 py-2 rounded-lg text-[12px] font-bold text-black"
                  style="background: linear-gradient(135deg,#fbbf24,#d97706); box-shadow: 0 0 12px rgba(251,191,36,0.3);"
                  @click="$refs.logoInput.click()"
                >📤 {{ form.logo_url ? 'Cambiar' : 'Subir' }} imagen</button>
                <button
                  v-if="form.logo_url"
                  type="button"
                  class="w-full px-3 py-1.5 rounded-lg text-[11px] font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10"
                  @click="removeLogo"
                >🗑 Eliminar logo</button>
                <p class="text-[10px] text-slate-500">PNG/JPG · Máx 5MB · Cuadrado idealmente.</p>
              </div>
            </div>
          </div>

          <div>
            <label class="block text-[12px] font-semibold text-slate-600 dark:text-slate-400 mb-1.5 uppercase tracking-wide">
              Nombre
            </label>
            <input
              v-model="form.name"
              type="text"
              placeholder="Santo Domingo Oeste"
              class="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm focus:outline-none focus:border-yellow-500"
            />
          </div>
          <div>
            <label class="block text-[12px] font-semibold text-slate-600 dark:text-slate-400 mb-1.5 uppercase tracking-wide">
              Código (3-4 letras)
            </label>
            <input
              v-model="form.code"
              type="text"
              maxlength="6"
              placeholder="SDO"
              class="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm uppercase font-bold focus:outline-none focus:border-yellow-500"
              :disabled="isEditing"
            />
            <p v-if="isEditing" class="text-[11px] text-slate-500 mt-1">
              El código no se puede cambiar después de creado
            </p>
          </div>
          <label class="flex items-center gap-2 cursor-pointer">
            <input v-model="form.active" type="checkbox" class="w-4 h-4" />
            <span class="text-sm text-slate-700 dark:text-slate-300">Sucursal activa</span>
          </label>
          <p v-if="errorMessage" class="text-sm text-red-600 dark:text-red-400">{{ errorMessage }}</p>
        </div>

        <div class="flex justify-end gap-2 mt-6">
          <button
            type="button"
            class="px-4 py-2 rounded-lg text-sm font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
            @click="closeModal"
          >Cancelar</button>
          <button
            type="button"
            class="px-4 py-2 rounded-lg text-sm font-semibold text-black"
            style="background: linear-gradient(135deg,#fbbf24,#d97706);"
            :disabled="isSaving"
            @click="save"
          >{{ isSaving ? 'Guardando…' : (isEditing ? 'Guardar cambios' : 'Crear sucursal') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>
